/**
 * SettingsSection - "Settings" form section molecule.
 *
 * Renders the framed section card that owns completion gates +
 * recurring schedule + checklist assignment for both work orders and
 * inspections, plus estimate capture on work orders only. Used by the
 * JobFormTemplate so the Settings experience stays consistent between
 * the two form types.
 *
 * Composition:
 *	 - Signature Required toggle (work orders only — customers never
 *	   sign inspections)
 *	 - Frequency dropdown (One-Time / Daily / Weekly / ...)
 *	 - Checklist template assignment dropdown
 *	 - EstimateFieldset (work orders only — inspections carry no pricing)
 *
 * The "auto-enabled for construction" hint on Signature Required is
 * shown only when relevant — work-order forms pass `signatureNote` to
 * surface the explanation in context.
 */
import React from 'react';
import { Checkbox } from '../../atoms';
import { SectionCard } from '../SectionCard';
import { EstimateFieldset } from '../EstimateFieldset';
import type { EstimateFieldsetValue } from '../EstimateFieldset';
import { RecurrencePicker } from '../RecurrencePicker';
import type { RecurrenceMode, RecurrenceRule } from '../../../utils/recurrence';
import type { ChecklistTemplate } from '../../../services/checklistTemplateService';

/* ============================================
 * TYPE DEFINITIONS
 * ============================================ */

/** Frequency option shape — kept exported for any legacy callers; the
 *  in-form Frequency control is now driven by the RecurrencePicker
 *  rather than a flat dropdown, so new code should not depend on it. */
export interface SettingsFrequencyOption {
	value: string;
	label: string;
}

export interface SettingsSectionProps {
	/** 'work_order' or 'inspection' — drives the checklist help-text copy. */
	formType: 'work_order' | 'inspection';
	/**
	 * "Send to Client" — emails the completed inspection to the customer
	 * contact. Inspection forms only. Defaults OFF by design: a poor
	 * inspection gets an internal review before the property manager sees it.
	 */
	notifyClient?: boolean;
	onNotifyClientChange?: (value: boolean) => void;

	signatureRequired: boolean;
	onSignatureRequiredChange: (value: boolean) => void;
	/** Optional note rendered next to the Signature label (e.g. construction auto-enable). */
	signatureNote?: string | null;

	/** RecurrencePicker driver — controlled by the parent JobForm. */
	recurrenceMode: RecurrenceMode;
	recurrenceRule: RecurrenceRule;
	onRecurrenceChange: (next: { mode: RecurrenceMode; rule: RecurrenceRule }) => void;
	/** YYYY-MM-DD start-date anchor — flows in from the form's
	 *  scheduled date so the preset labels read correctly. */
	recurrenceStartDate: string;
	/** When false, the picker is hidden entirely. Used to suppress
	 *  recurrence configuration in edit mode — a single occurrence
	 *  shouldn't be able to change the parent series' schedule from
	 *  the per-record edit form (that's done on the Repeats detail
	 *  page instead). Create + Duplicate flows pass true. */
	allowRecurrence?: boolean;

	/** Currently-selected checklist template id ('' = none). */
	checklistTemplateId: string;
	onChecklistTemplateIdChange: (id: string) => void;
	/** Templates the current user is allowed to assign for this form type. */
	assignableChecklists: Pick<ChecklistTemplate, 'id' | 'title'>[];

	/** Estimate sub-fields managed by the EstimateFieldset molecule.
	 *  Only rendered on work-order forms — inspections don't capture
	 *  pricing — but the props stay required so JobForm can keep a
	 *  single wiring for both form types. */
	estimate: EstimateFieldsetValue;
	onEstimateChange: (next: EstimateFieldsetValue) => void;

	/** When true, the section renders only the per-WO settings that
	 *  still live on the child WO: Signature Required and Checklist.
	 *  Frequency + Estimate are hidden because in slim mode they're
	 *  owned by the parent Project Work Order. Used by the child-WO
	 *  create flow under a Project. */
	slim?: boolean;

	/** Field-level errors keyed by camelCase field name. The Settings
	 *  section consumes `checklistTemplateId` to surface the required-
	 *  field error inline; other sections own their own keys. */
	errors?: Record<string, string>;

	/** Standard styling tokens. */
	labelClass: string;
	inputClass: string;
	normalBorder: string;
	/** Border + ring classes applied when a field has an error.
	 *  Owned by JobForm so the visual error treatment matches every
	 *  other section. */
	errorBorder?: string;
	/** Optional extra classes for the outer wrapper. */
	className?: string;
}

/* ============================================
 * COMPONENT
 * ============================================ */

export const SettingsSection: React.FC<SettingsSectionProps> = ({
	formType,
	notifyClient = false,
	onNotifyClientChange,
	signatureRequired,
	onSignatureRequiredChange,
	signatureNote,
	recurrenceMode,
	recurrenceRule,
	onRecurrenceChange,
	recurrenceStartDate,
	allowRecurrence = true,
	checklistTemplateId,
	onChecklistTemplateIdChange,
	assignableChecklists,
	estimate,
	onEstimateChange,
	slim = false,
	errors,
	labelClass,
	inputClass,
	normalBorder,
	errorBorder,
	className = '',
}) => {
	/* Per-field error lookup — keeps the JSX terse. The fallback empty
	   string makes the border + message branches read cleanly. */
	const checklistError = errors?.checklistTemplateId || '';
	/* The checklist help text reflects the audience — technicians for
	   work orders, inspectors for inspections. The semantic is the
	   same: a snapshot of the template is attached to the record. */
	const checklistHelp = formType === 'work_order'
		? 'Sends a snapshot to the assigned technician(s). Editing the template later won\'t affect this work order.'
		: 'Sends a snapshot to the inspector. Editing the template later won\'t affect this inspection.';

	return (
		<SectionCard title="Settings" className={`settings-section ${className}`} dataRef="molecule-settings-section">
			<div className="settings-section__body space-y-16px">
				{/* Signature toggle — work orders only. Customers sign work
				    orders, never inspections, so the setting is not offered
				    there (beta feedback, Oct 2026). */}
				{formType === 'work_order' && (
				<Checkbox
					checked={signatureRequired}
					onChange={onSignatureRequiredChange}
					className="settings-section__signature items-center"
					labelClassName="font-medium text-gray-700"
					dataRef="molecule-settings-section-signature"
					label={(
						<>
							Signature Required
							{signatureNote && <span className="text-12px font-normal text-brand-text-muted ml-4px">{signatureNote}</span>}
						</>
					)}
				/>
				)}

				{/* "Send to Client" — inspections only.
				    Unchecked by default and deliberately so: the inspection is
				    reviewed internally first, and only shared with the property
				    manager once someone has decided it should be. The email
				    goes out when the inspection is marked completed. */}
				{formType === 'inspection' && onNotifyClientChange && (
					<div className="settings-section__notify-client">
						<Checkbox
							checked={notifyClient}
							onChange={onNotifyClientChange}
							label="Send to Client"
							description="Emails the finished inspection to the customer contact when it is completed. Leave off to review the results internally first."
							className="settings-section__notify-client-toggle py-10px"
							labelClassName="font-medium text-gray-700"
							dataRef="molecule-settings-section-notify-client"
						/>
					</div>
				)}

				{/* Frequency — Google-Calendar-style RecurrencePicker.
				    Hidden in slim mode (parent Project WO owns the
				    recurrence) and in edit mode (an existing record
				    can't change its series schedule from here — that
				    happens on the Repeats detail page). Create and
				    Duplicate flows render the picker normally. */}
				{!slim && allowRecurrence && (
					<RecurrencePicker
						mode={recurrenceMode}
						rule={recurrenceRule}
						startDate={recurrenceStartDate}
						onChange={onRecurrenceChange}
						labelClass={labelClass}
						inputClass={inputClass}
						normalBorder={normalBorder}
					/>
				)}

				{/* Checklist assignment dropdown — REQUIRED across both WO
				    and Inspection forms. JobForm enforces the same rule
				    via validateForm(); the recurrence-series backend
				    requires it on the template too so every materialized
				    occurrence gets a checklist snapshot. */}
				<div className="max-w-sm">
					<label htmlFor="checklistTemplateId" className={labelClass}>
						Checklist <span className="text-red-600" aria-hidden="true">*</span>
					</label>
					<select
						id="checklistTemplateId"
						value={checklistTemplateId}
						onChange={(e) => onChecklistTemplateIdChange(e.target.value)}
						aria-invalid={checklistError ? true : undefined}
						aria-describedby={checklistError ? 'checklistTemplateId-error' : undefined}
						className={`${inputClass} bg-white ${checklistError && errorBorder ? errorBorder : normalBorder}`}
					>
						<option value="">Select checklist...</option>
						{assignableChecklists.map((cl) => (
							<option key={cl.id} value={cl.id}>{cl.title}</option>
						))}
					</select>
					{checklistError ? (
						<p id="checklistTemplateId-error" className="mt-4px text-12px font-sans font-medium text-red-600">{checklistError}</p>
					) : (
						<p className="mt-4px text-12px font-sans text-brand-text-muted">{checklistHelp}</p>
					)}
				</div>

				{/* Estimate — captures cost (and optional range). Approval
				    is handled separately on the detail page. Work orders
				    only: inspections are an assessment, not a priced job,
				    so no cost or range is collected for them. Also hidden
				    in slim mode, where the estimate belongs to the parent
				    project rather than the child WO. */}
				{!slim && formType === 'work_order' && (
					<div className="max-w-md pt-12px border-t border-brand-border">
						<EstimateFieldset
							value={estimate}
							onChange={onEstimateChange}
						/>
					</div>
				)}
			</div>
		</SectionCard>
	);
};

export default SettingsSection;
